import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/Button';
import { SandboxWebhookDelivery } from '@/services/api/sandbox';
import { WebhookEndpointRow } from '@/services/api/webhooks';
import {
  listShipmentWebhookDeliveries,
  listShipmentWebhooks,
  retryShipmentWebhook,
  ShipmentWebhookRow,
  ShipmentWebhooksFilter,
  ShipmentWebhooksPage,
} from '@/services/api/webhookDeliveries';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import {
  DeliveryStatusPill,
  formatTime,
  statusLabel,
} from './sandbox/sandboxUi';
import WebhookDeliveriesPanel, {
  deliveryCause,
  retryResultOf,
  WebhookRetryResult,
} from './sandbox/WebhookDeliveriesPanel';

const PAGE_SIZE = 20;
/** The API's own limit: one manual retry per shipment every 30 s. */
const RETRY_REST_MS = 30_000;

const FILTERS: { key: ShipmentWebhooksFilter; label: string }[] = [
  { key: 'failing', label: 'Not delivered' },
  { key: 'all', label: 'All' },
];

/** A retry asked for, waiting for the Production confirm. */
interface PendingRetry {
  row: ShipmentWebhookRow;
  rowKey: string;
}

/** Sending to Production is real: the partner's system receives it again. */
const ConfirmProductionRetry: React.FC<{
  pending: PendingRetry | null;
  url: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ pending, url, onCancel, onConfirm }) => {
  if (!pending) return null;
  const { row } = pending;
  const event = `#${row.latest.sequence} ${
    row.latest.eventStatus
      ? statusLabel(row.latest.eventStatus)
      : row.latest.eventType
  }`;
  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={onCancel}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-production-retry-title"
          className="w-full max-w-md rounded-xl bg-bg-white-0 px-6 py-5 shadow-card-lg"
        >
          <div className="flex items-center gap-2">
            <i className="ri-send-plane-line text-lg text-warning-base" />
            <h2
              id="confirm-production-retry-title"
              className="text-lg font-semibold text-text-strong-950"
            >
              Send to Production again?
            </h2>
          </div>
          <p className="mt-2 text-sm text-text-sub-600">
            {event} of{' '}
            <span className="font-mono text-xs text-text-strong-950">
              {row.shipment.waybill}
            </span>{' '}
            goes to{' '}
            <span className="break-all font-mono text-xs text-text-strong-950">
              {url}
            </span>{' '}
            now, with the same event id. Your production system receives it
            again — it should dedupe on{' '}
            <code className="font-mono">event.id</code>.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="white" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onConfirm}>Send again</Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

/**
 * "See shipments" of the active Production endpoint: the client's live
 * shipments with their latest webhook — not delivered first by default — and,
 * opened, the same webhook panel as the Sandbox, where the latest one can be
 * retried (after a confirm: it reaches the partner's production system).
 */
const ProductionShipmentWebhooks: React.FC<{
  endpoint: WebhookEndpointRow;
  onBack: () => void;
}> = ({ endpoint, onBack }) => {
  const [filter, setFilter] = useState<ShipmentWebhooksFilter>('failing');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState<ShipmentWebhooksPage | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<
    Record<string, SandboxWebhookDelivery[] | null>
  >({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, WebhookRetryResult>>(
    {}
  );
  const [restUntil, setRestUntil] = useState<Record<string, number>>({});
  const [pending, setPending] = useState<PendingRetry | null>(null);
  const [now, setNow] = useState(Date.now());

  // The countdown on a resting Retry ticks only while one rests.
  const resting = Object.values(restUntil).some((t) => t > now);
  useEffect(() => {
    if (!resting) return undefined;
    const t = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(t);
  }, [resting, now]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setList(
        await listShipmentWebhooks('live', {
          status: filter,
          search,
          page,
          limit: PAGE_SIZE,
        })
      );
      setListError(null);
    } catch (err) {
      setListError(
        extractApiErrorMessage(err, 'Production webhooks could not be loaded')
      );
    } finally {
      setLoading(false);
    }
  }, [filter, search, page]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadDeliveries = useCallback(async (shipmentId: string) => {
    try {
      const rows = await listShipmentWebhookDeliveries('live', shipmentId);
      setDeliveries((d) => ({ ...d, [shipmentId]: rows }));
    } catch {
      setDeliveries((d) => ({ ...d, [shipmentId]: null }));
    }
  }, []);

  const toggle = (shipmentId: string) => {
    const next = open === shipmentId ? null : shipmentId;
    setOpen(next);
    if (next && deliveries[next] === undefined) loadDeliveries(next);
  };

  const sendRetry = async ({ row, rowKey }: PendingRetry) => {
    const id = row.shipment.id;
    setPending(null);
    setBusyId(id);
    const at = new Date().toISOString();
    try {
      const answer = await retryShipmentWebhook('live', id);
      setResults((r) => ({ ...r, [id]: retryResultOf(answer, rowKey, at) }));
      // The API counts any attempt and answers 429 inside its window.
      if (answer.status < 400 || answer.status === 429) {
        setRestUntil((t) => ({ ...t, [id]: Date.now() + RETRY_REST_MS }));
        setNow(Date.now());
      }
    } catch (err) {
      setResults((r) => ({
        ...r,
        [id]: {
          rowKey,
          at,
          outcome: 'error',
          title: 'Retry failed',
          detail: extractApiErrorMessage(err, 'Could not reach the API'),
        },
      }));
    } finally {
      setBusyId(null);
      // The row's webhooks and the list's verdict both moved.
      loadDeliveries(id);
      loadList();
    }
  };

  const changeFilter = (next: ShipmentWebhooksFilter) => {
    setFilter(next);
    setPage(1);
    setOpen(null);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
    setOpen(null);
  };

  const rows = list?.data ?? [];
  const pagination = list?.pagination;

  return (
    <section className="rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-6">
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-text-sub-600 hover:text-text-strong-950"
      >
        <i className="ri-arrow-left-line" />
        Webhook endpoints
      </button>
      <h2 className="text-base font-semibold text-text-strong-950">
        Production webhooks
      </h2>
      <p className="mt-0.5 text-xs text-text-sub-600">
        Live shipments and their latest webhook to{' '}
        <span className="break-all font-mono">{endpoint.url}</span>. A latest
        webhook that did not arrive can be sent again from its shipment — to
        your production system, so it asks first.
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-lg bg-bg-weak-50 p-1" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => changeFilter(f.key)}
              className={`rounded-md px-3 py-1 text-xs font-medium ${
                filter === f.key
                  ? 'bg-bg-white-0 text-text-strong-950 shadow-sm'
                  : 'text-text-sub-600 hover:text-text-strong-950'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <form onSubmit={submitSearch} className="flex gap-2">
          <input
            type="search"
            aria-label="Search waybill or booking id"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Waybill or booking id"
            className="w-56 rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-1.5 text-sm text-text-strong-950 placeholder:text-text-soft-400 focus:border-primary focus:outline-none"
          />
          <Button variant="white" size="small" type="submit">
            Search
          </Button>
        </form>
      </div>

      {listError ? (
        <p className="mt-6 text-sm text-error-dark">{listError}</p>
      ) : loading && !list ? (
        <p className="mt-6 text-sm text-text-soft-400">Loading webhooks…</p>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-stroke-sub-300 px-6 py-10 text-center">
          <p className="text-sm font-medium text-text-strong-950">
            {search
              ? 'No shipment matches that search'
              : filter === 'failing'
                ? 'Every latest webhook arrived'
                : 'No production webhooks yet'}
          </p>
          {filter === 'failing' && !search && (
            <p className="mt-1 text-xs text-text-sub-600">
              Nothing to retry. “All” lists every shipment that sent one.
            </p>
          )}
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-stroke-soft-200 rounded-lg border border-stroke-soft-200">
          {rows.map((row) => {
            const id = row.shipment.id;
            const expanded = open === id;
            const cause = deliveryCause(row.latest.delivery);
            const restLeft = Math.max(
              0,
              Math.ceil(((restUntil[id] ?? 0) - now) / 1000)
            );
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-expanded={expanded}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-bg-weak-50"
                >
                  <span className="min-w-[180px]">
                    <span className="block font-mono text-xs font-semibold text-text-strong-950">
                      {row.shipment.waybill}
                    </span>
                    <span className="block font-mono text-[11px] text-text-soft-400">
                      {row.shipment.bookingId} ·{' '}
                      {statusLabel(row.shipment.status)}
                    </span>
                  </span>
                  <span className="min-w-[120px] text-sm text-text-strong-950">
                    #{row.latest.sequence}{' '}
                    {row.latest.eventStatus
                      ? statusLabel(row.latest.eventStatus)
                      : row.latest.eventType}
                  </span>
                  <DeliveryStatusPill status={row.latest.delivery.status} />
                  {cause && (
                    <span className="rounded bg-error-lighter px-1.5 py-0.5 font-mono text-[11px] font-semibold text-error-dark">
                      {cause}
                    </span>
                  )}
                  {row.retryable && (
                    <span className="rounded-full bg-warning-lighter px-2 py-0.5 text-[11px] font-medium text-warning-dark">
                      Can retry
                    </span>
                  )}
                  <span className="ml-auto text-xs tabular-nums text-text-sub-600">
                    {formatTime(row.latest.createdAt)}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-text-soft-400 transition-transform ${
                      expanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expanded && (
                  <div className="border-t border-stroke-soft-200 bg-bg-weak-50/40 p-4">
                    <WebhookDeliveriesPanel
                      deliveries={deliveries[id] ?? null}
                      loading={deliveries[id] === undefined}
                      shipmentStatus={row.shipment.status}
                      retry={{
                        onRetry: (rowKey) => setPending({ row, rowKey }),
                        busy: busyId === id,
                        waitSeconds: restLeft,
                        result: results[id] ?? null,
                      }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pagination && pagination.lastPage > 1 && (
        <div className="mt-3 flex items-center justify-between text-xs text-text-sub-600">
          <span>
            Page {pagination.page} of {pagination.lastPage} · {pagination.total}{' '}
            shipments
          </span>
          <div className="flex gap-2">
            <Button
              variant="white"
              size="small"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="white"
              size="small"
              disabled={page >= pagination.lastPage || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ConfirmProductionRetry
        pending={pending}
        url={endpoint.url}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && sendRetry(pending)}
      />
    </section>
  );
};

export default ProductionShipmentWebhooks;
